import * as React from 'react';
import { Popup } from 'semantic-ui-react';
import { useNavigate } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import moment from 'moment';
// import Popup from 'reactjs-popup';

import useLoadingStore from 'zustands/useLoadingStore';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';

import Button from 'common/button/button';
import CheckBox from 'common/checkbox/checkbox';
import WithSideBarAndNavBar from 'layouts/withSideBarAndNavBar/withSideBarAndNavBar';
import './employee-registration.scss';

// Assets
import { ReactComponent as ThreeDots } from '../../assets/icons/three_dots.svg';

// Data
import EmployeeRegData from '../../dummy-data/employee-registration.json';
import { IInvitedEmp, IInvitedEmpWithRegist } from 'types/employee-registration';
import { phoneNumberAddHypen } from 'constants/utils';
import useAlertConfirmStore from 'zustands/useAlertConfirmStore';

const employeeRegistration: React.FC = () => {
  const navigate = useNavigate();

  const { setLoadingState } = useLoadingStore();
  const { setModalState } = useAlertConfirmStore();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [invitedEmp, setInvitedEmp] = React.useState<IInvitedEmp[]>([]);
  const [empWithRegited, setEmpWithRegited] = React.useState<IInvitedEmpWithRegist[]>([]);
  const [allSelectCheckbox, setAllSelectCheckbox] = React.useState<boolean>(false);
  const [allEmployeeSelectCheckbox, setAllEmployeeSelectCheckbox] = React.useState<boolean>(false);

  const fetchData = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/getinviteEmps`);

      if (data.resultCode === 0) {
        setEmpWithRegited(
          data.resultData.emps.map((i: any) => {
            return {
              empSeq: i.EMP_SEQ,
              empName: i.EMP_NAME,
              empPhone: i.MobileNo,
              invitedDate: i.invitationDate,
              isChecked: false,
            };
          }),
        );
        setInvitedEmp(
          data.resultData.join_emps.map((i: any) => {
            return {
              joinEmpSeq: i.join_emp_seq,
              empName: i.NAME,
              empPhone: i.PHONE,
              invitedDate: i.regdate ? moment(i.regdate).toDate() : '',
              isChecked: false,
            };
          }),
        );
        setAllSelectCheckbox(false);
        setAllEmployeeSelectCheckbox(false);
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/getinviteEmps');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 미가입 직원목록 전체 체크/해제
  const checkboxInviteAllToggle = (event: any) => {
    if (allSelectCheckbox) {
      const empData = invitedEmp.map((data: any) => {
        return { ...data, isChecked: false };
      });
      setInvitedEmp(empData);
    } else {
      const empData = invitedEmp.map((data: any) => {
        return { ...data, isChecked: true };
      });
      setInvitedEmp(empData);
    }
    setAllSelectCheckbox(!allSelectCheckbox);
  };
  // 미가입 직원목록 아이템 체크 시 토글
  const checkboxInviteToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const empData = invitedEmp.map((i) => {
      if (i.joinEmpSeq.toString() === event.target.name.split('_')[0]) {
        return { ...i, isChecked: !i.isChecked };
      }
      return i;
    });
    const allCheck = empData.findIndex((i) => !i.isChecked);
    if (allCheck === -1) {
      setAllSelectCheckbox(true);
    } else {
      setAllSelectCheckbox(false);
    }
    setInvitedEmp(empData);
  };

  // 승인대기중 직원목록 전체 체크/해제
  const checkboxEmployeeAllToggle = (event: any) => {
    if (allEmployeeSelectCheckbox) {
      const empData = empWithRegited.map((data: any) => {
        return { ...data, isChecked: false };
      });
      setEmpWithRegited(empData);
    } else {
      const empData = empWithRegited.map((data: any) => {
        return { ...data, isChecked: true };
      });
      setEmpWithRegited(empData);
    }
    setAllEmployeeSelectCheckbox(!allEmployeeSelectCheckbox);
  };

  // 승인대기중 직원목록 아이템 체크 시 토글
  const checkboxEmployeeToggle = (event: any) => {
    const empData = empWithRegited.map((i) => {
      if (i.empSeq.toString() === event.target.name.split('_')[0]) {
        return { ...i, isChecked: !i.isChecked };
      }
      return i;
    });
    const allCheck = empData.findIndex((i) => !i.isChecked);
    if (allCheck === -1) {
      setAllEmployeeSelectCheckbox(true);
    } else {
      setAllEmployeeSelectCheckbox(false);
    }
    setEmpWithRegited(empData);
  };

  // 초대하기 화면 이동
  const enterSingleInviteScreen = () => {
    navigate(`/register-employee-card`);
  };

  // 엑셀로 초대 화면 이동
  const bulkInvite = () => {
    navigate(`/bulk-registration`);
  };

  // 미가입 직원 더보기 메뉴 클릭
  const handleInvite = (type: string, emp: IInvitedEmp) => {
    if (type === 'RESEND') {
      resendSelectedInvite([{ NAME: emp.empName, PHONE: emp.empPhone }]);
    }
    if (type === 'DELETE') {
      removeSelectedInvite(emp.joinEmpSeq);
    }
  };

  // 재초대 요청
  const resendSelectedInvite = async (emps: { NAME: string; PHONE: string }[] = []) => {
    try {
      const checkEmps = invitedEmp.filter((i) => i.isChecked).map((i) => ({ NAME: i.empName, PHONE: i.empPhone }));
      if (invitedEmp.length === 0) {
        return setModalState({ isModalShow: true, title: '직원초대 재전송', desc: '초대받은 직원이 없습니다' });
      } else if (checkEmps.length === 0) {
        return setModalState({ isModalShow: true, title: '직원초대 재전송', desc: '직원을 선택해 주세요' });
      } else {
        setLoadingState({ isFetchLoading: true });
        const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/reinviteEmp`, {
          LIST: emps.length > 0 ? emps : checkEmps,
        });

        if (data.resultCode === 0) {
          setModalState({ isModalShow: true, title: '직원초대 재전송', desc: '완료되었습니다' });
          fetchData();
        } else {
          throw new Error(data.resultMsg);
        }
      }
    } catch (err) {
      checkErrorLog(err, ' /auth/reinviteEmp');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 미가입 직원 삭제 요청
  const removeSelectedInvite = (joinEmpSeq: number = 0) => {
    try {
      const completeFn = async () => {
        const checkEmps = invitedEmp.filter((i) => i.isChecked).map((i) => ({ join_emp_seq: i.joinEmpSeq }));
        if (invitedEmp.length === 0) {
          setTimeout(() => {
            return setModalState({ isModalShow: true, title: '초대직원 삭제', desc: '초대받은 직원이 없습니다' });
          }, 300);
        } else if (joinEmpSeq === 0 && checkEmps.length === 0) {
          setTimeout(() => {
            return setModalState({ isModalShow: true, title: '초대직원 삭제', desc: '직원을 선택해 주세요' });
          }, 300);
        } else {
          setLoadingState({ isFetchLoading: true });
          const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/canceljoin`, {
            LIST: joinEmpSeq !== 0 ? [{ join_emp_seq: joinEmpSeq }] : checkEmps,
          });

          if (data.resultCode === 0) {
            setModalState({ isModalShow: true, title: '초대직원 삭제', desc: '완료되었습니다' });
            fetchData();
          } else {
            throw new Error(data.resultMsg);
          }
        }
      };
      setModalState({
        isModalShow: true,
        title: '초대 직원 삭제',
        desc: '해당 직원의 초대 내역을 삭제합니다',
        okBtnText: '확인',
        okCallback: completeFn,
        isWarning: true,
      });
    } catch (err) {
      checkErrorLog(err, ' /auth/canceljoin');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 승인대기중 직원 더보기 메뉴 클릭
  const handleAcceptRejectEmployee = (type: string, i: IInvitedEmpWithRegist) => {
    if (type === 'ACCEPT') {
      acceptSelectedEmployee(i);
    }
    if (type === 'REJECT') {
      rejectSelectedEmployee(i.empSeq);
    }
  };

  // 승인대기중 직원 합류 거절
  const rejectSelectedEmployee = (empSeq: number = 0) => {
    try {
      const requestFn = async () => {
        const checkEmps = empWithRegited.filter((i) => i.isChecked).map((i) => ({ EMP_SEQ: i.empSeq }));
        if (empWithRegited.length === 0) {
          setTimeout(() => {
            return setModalState({ isModalShow: true, title: '직원 합류 거절', desc: '승인대기 중인 직원이 없습니다' });
          }, 300);
        } else if (empSeq === 0 && checkEmps.length === 0) {
          setTimeout(() => {
            return setModalState({ isModalShow: true, title: '직원 합류 거절', desc: '직원을 선택해 주세요' });
          }, 300);
        } else {
          setLoadingState({ isFetchLoading: true });
          const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/delinviteEmp`, {
            LIST: empSeq !== 0 ? [{ EMP_SEQ: empSeq }] : checkEmps,
          });

          if (data.resultCode === 0) {
            setModalState({ isModalShow: true, title: '직원 합류 거절', desc: '완료되었습니다' });
            fetchData();
          } else {
            throw new Error(data.resultMsg);
          }
        }
      };

      setModalState({
        isModalShow: true,
        title: '직원 합류 거절',
        desc: '해당 직원의 합류를 거절하고 목록에서 삭제합니다',
        okBtnText: '확인',
        okCallback: requestFn,
        isWarning: true,
      });
    } catch (err) {
      checkErrorLog(err, ' /auth/delinviteEmp');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  const acceptSelectedEmployee = (empInfo?: IInvitedEmpWithRegist) => {
    const checkEmps = empWithRegited.filter((data) => data.isChecked);
    if (empWithRegited.length === 0) {
      setTimeout(() => {
        return setModalState({ isModalShow: true, title: '직원 합류 승인', desc: '승인대기 중인 직원이 없습니다' });
      }, 300);
    } else if (!empInfo && checkEmps.length === 0) {
      setTimeout(() => {
        return setModalState({ isModalShow: true, title: '직원 합류 거절', desc: '직원을 선택해 주세요' });
      }, 300);
    } else {
      navigate('/staff-registration', {
        state: {
          empList: empInfo ? [empInfo] : checkEmps,
        },
      });
    }
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  const popupStyle = {
    borderRadius: 8,
    width: '138px',
    height: '84px',
    border: '1px solid #E9E9E9',
    boxShadow: '2px 2px 20px rgba(0, 0, 0, 0.06)',
  };

  return (
    <WithSideBarAndNavBar>
      <div className="employee-registration">
        <div className="reg-head">
          <div>직원초대</div>
          <div className="reg-head__buttons">
            <div className="reg-head__invite">
              <Button label="+초대하기" height={38} type="type2" action={enterSingleInviteScreen} />
            </div>
            <div>
              <Button label="엑셀로 일괄초대" height={38} type="type1" action={bulkInvite} />
            </div>
          </div>
        </div>
        <div className="reg-table">
          <table className="reg-table__table">
            <tr>
              <td className="reg-table-td">
                <div className="reg-card">
                  <div className="reg-card__header">
                    <div className="reg-card__head">
                      미가입 <span className="reg-num">{invitedEmp.length}</span>
                    </div>
                    <div className="reg-card__subhead">아직 회원가입을 하지 않은 직원의 목록입니다.</div>
                  </div>

                  <div className="table-height">
                    <table className="table-reg">
                      <colgroup>
                        <col width="10%" />
                        <col width="20%" />
                        <col width="30%" />
                        <col width="20%" />
                        <col width="20%" />
                      </colgroup>
                      <thead>
                        <tr className="table-reg__tr">
                          <th className="table-reg__td-center">
                            <CheckBox checked={checkboxInviteAllToggle} defaultCheck={allSelectCheckbox} />
                          </th>
                          <th className="table-reg__td">이름</th>
                          <th className="table-reg__td">핸드폰 번호</th>
                          <th className="table-reg__td">초대날짜</th>
                          <th className="table-reg__td-center">더보기</th>
                        </tr>
                      </thead>
                    </table>
                    {invitedEmp.length > 0 ? (
                      <div style={{ height: 'calc(50vh - 51px)', overflowY: 'scroll', overflowX: 'hidden' }}>
                        <table className="table-reg" style={{ width: 'calc(100% + 10px)' }}>
                          <colgroup>
                            <col width="10%" />
                            <col width="20%" />
                            <col width="30%" />
                            <col width="20%" />
                            <col width="20%" />
                          </colgroup>
                          <tbody>
                            {invitedEmp.map((i) => {
                              return (
                                <tr className="table-reg__tr" key={`${i.joinEmpSeq}_${i.empPhone}`}>
                                  <td className="table-reg__td-center" style={{ borderTopColor: '#e9e9e9' }}>
                                    <CheckBox
                                      checked={checkboxInviteToggle}
                                      defaultCheck={i?.isChecked ?? false}
                                      checkBoxname={`${i.joinEmpSeq}_${i.empPhone}`}
                                    />
                                  </td>
                                  <td className="table-reg__td__table-name">{i.empName}</td>
                                  <td className="table-reg__td__table-content">{phoneNumberAddHypen(i.empPhone)}</td>
                                  <td className="table-reg__td__table-content">
                                    {i.invitedDate ? moment(i.invitedDate).format('YYYY.MM.DD') : ''}
                                  </td>
                                  <td className="table-reg__td-center" style={{ borderTopColor: '#e9e9e9' }}>
                                    <Popup
                                      on="click"
                                      trigger={
                                        <div className="employee-registration-popup__icon">
                                          <ThreeDots />
                                        </div>
                                      }
                                      style={popupStyle}
                                      position="bottom center"
                                      basic
                                      flowing
                                      hoverable>
                                      <div>
                                        <div
                                          className="employee-registration-popup__resend-invite"
                                          onClick={() => {
                                            handleInvite('RESEND', i);
                                          }}>
                                          초대문자 재전송
                                        </div>
                                        <div
                                          className="employee-registration-popup__delete-invite"
                                          onClick={() => {
                                            handleInvite('DELETE', i);
                                          }}>
                                          초대 리스트 삭제
                                        </div>
                                      </div>
                                    </Popup>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="emptyArea">
                        <span className="emptyText">초대받은 직원이 없습니다.</span>
                      </div>
                    )}
                  </div>
                  <div className="table-reg__buttons">
                    <div className="table-reg__buttons__cancel">
                      <Button label="삭제" type="type1" action={() => removeSelectedInvite()} height={38} />
                    </div>
                    <div>
                      <Button label="초대문자 재전송" type="type3" action={resendSelectedInvite} height={38} />
                    </div>
                  </div>
                </div>
              </td>
              <td className="reg-table-td">
                <div className="reg-card">
                  <div className="reg-card__header">
                    <div className="reg-card__head">
                      승인대기중 <span className="reg-num">{empWithRegited.length}</span>
                    </div>
                    <div className="reg-card__subhead">회원가입 완료 후, 사업주의 승인을 대기중입니다.</div>
                  </div>
                  <div>
                    <div className="table-height">
                      <table className="table-reg">
                        <colgroup>
                          <col width="10%" />
                          <col width="20%" />
                          <col width="30%" />
                          <col width="20%" />
                          <col width="20%" />
                        </colgroup>
                        <thead>
                          <tr className="table-reg__tr">
                            <th className="table-reg__td-center">
                              <CheckBox checked={checkboxEmployeeAllToggle} defaultCheck={allEmployeeSelectCheckbox} />
                            </th>
                            <th className="table-reg__td">이름</th>
                            <th className="table-reg__td">핸드폰 번호</th>
                            <th className="table-reg__td">초대날짜</th>
                            <th className="table-reg__td-center">더보기</th>
                          </tr>
                        </thead>
                      </table>
                      {empWithRegited.length > 0 ? (
                        <div style={{ height: 'calc(50vh - 51px)', overflowY: 'scroll', overflowX: 'hidden' }}>
                          <table className="table-reg" style={{ width: 'calc(100% + 10px)' }}>
                            <colgroup>
                              <col width="10%" />
                              <col width="20%" />
                              <col width="30%" />
                              <col width="20%" />
                              <col width="20%" />
                            </colgroup>
                            <tbody>
                              {empWithRegited.map((i) => (
                                <tr className="table-reg__tr" key={`${i.empSeq}_${i.empPhone}`}>
                                  <td className="table-reg__td-center" style={{ borderTopColor: '#e9e9e9' }}>
                                    <CheckBox
                                      checked={checkboxEmployeeToggle}
                                      defaultCheck={i?.isChecked ?? false}
                                      checkBoxname={`${i.empSeq}_${i.empPhone}`}
                                    />
                                  </td>
                                  <td className="table-reg__td__table-name">{i.empName}</td>
                                  <td className="table-reg__td__table-content">{phoneNumberAddHypen(i.empPhone)}</td>
                                  <td className="table-reg__td__table-content">
                                    {i.invitedDate ? moment(i.invitedDate).format('YYYY.MM.DD') : ''}
                                  </td>
                                  <td className="table-reg__td-center" style={{ borderTopColor: '#e9e9e9' }}>
                                    <Popup
                                      on="click"
                                      trigger={
                                        <div className="employee-registration-popup__icon">
                                          <ThreeDots />
                                        </div>
                                      }
                                      style={popupStyle}
                                      position="bottom center"
                                      basic
                                      flowing
                                      hoverable>
                                      <div>
                                        <div
                                          className="employee-registration-popup__resend-invite"
                                          onClick={() => {
                                            handleAcceptRejectEmployee('ACCEPT', i);
                                          }}>
                                          합류 승인
                                        </div>
                                        <div
                                          className="employee-registration-popup__delete-invite"
                                          onClick={() => {
                                            handleAcceptRejectEmployee('REJECT', i);
                                          }}>
                                          합류 거절
                                        </div>
                                      </div>
                                    </Popup>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="emptyArea">
                          <span className="emptyText">승인대기중인 직원이 없습니다.</span>
                        </div>
                      )}
                    </div>
                    <div className="table-reg__buttons">
                      <div className="table-reg__buttons__cancel">
                        <Button label="합류거절" type="type1" action={() => rejectSelectedEmployee()} height={38} />
                      </div>
                      <div>
                        <Button label="합류승인" type="type3" action={() => acceptSelectedEmployee()} height={38} />
                      </div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </table>
        </div>
      </div>
    </WithSideBarAndNavBar>
  );
};
export default employeeRegistration;
